import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type HubSearchOption = {
  readonly value: string;
  readonly label: string;
};

type HubSearchSelectProps = {
  readonly value: string;
  readonly options: readonly HubSearchOption[];
  readonly onChange: (value: string) => void;
  readonly ariaLabel: string;
  readonly placeholder?: string;
  /** Optional key prefix like "Paysage :" — omit for topbar landscape. */
  readonly keyLabel?: string;
  readonly testId?: string;
  readonly className?: string;
  readonly disabled?: boolean;
};

type PopRect = { top: number; left: number; width: number };

/**
 * Single-select dropdown matching the platform "Filtres" popover
 * (`nbs-filter-token` + searchable `nbs-filter-pop`).
 * Renders the menu in a portal so sibling panels (e.g. table) cannot cover it.
 */
export function HubSearchSelect({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder = "Rechercher…",
  keyLabel,
  testId,
  className,
  disabled = false,
}: HubSearchSelectProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [popRect, setPopRect] = useState<PopRect | null>(null);
  const [cachedLabel, setCachedLabel] = useState<string | null>(null);

  const selectedFromOptions = options.find((o) => o.value === value);
  useEffect(() => {
    if (selectedFromOptions) {
      setCachedLabel(selectedFromOptions.label);
      return;
    }
    if (!value) setCachedLabel(null);
  }, [selectedFromOptions, value]);

  const selectedLabel = selectedFromOptions?.label ?? cachedLabel ?? (value ? value : "—");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useLayoutEffect(() => {
    if (!open || disabled) {
      setPopRect(null);
      return;
    }
    const update = () => {
      const el = rootRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = Math.max(r.width, 220);
      let left = r.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      setPopRect({
        top: r.bottom + 6,
        left,
        width,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, disabled]);

  useEffect(() => {
    if (!open || disabled) return;
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, disabled]);

  return (
    <div
      className={`nbs-filter-token nbs-hub-search-select${open ? " nbs-hub-search-select--open" : ""}${
        disabled ? " nbs-hub-search-select--disabled" : ""
      }${className ? ` ${className}` : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="nbs-filter-token__body nbs-hub-search-select__trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-disabled={disabled}
        disabled={disabled}
        data-testid={testId}
        onClick={() => {
          if (disabled) return;
          setQuery("");
          setOpen((v) => !v);
        }}
      >
        {keyLabel ? <span className="nbs-filter-token__key">{keyLabel} :</span> : null}
        <span className="nbs-filter-token__val">{selectedLabel}</span>
        <svg
          className="nbs-filter-token__caret"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && !disabled && popRect
        ? createPortal(
            <div
              ref={popRef}
              className="nbs-filter-pop nbs-hub-search-select__pop nbs-hub-search-select__pop--portal"
              role="listbox"
              id={listId}
              style={{
                position: "fixed",
                top: popRect.top,
                left: popRect.left,
                width: popRect.width,
                zIndex: 80,
              }}
            >
              <div className="nbs-filter-pop__searchwrap">
                <svg
                  className="nbs-filter-pop__search-ico"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3-3" />
                </svg>
                <input
                  className="nbs-filter-pop__search"
                  type="text"
                  placeholder={placeholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                {query ? (
                  <button
                    type="button"
                    className="nbs-filter-pop__clear"
                    aria-label="Effacer la recherche"
                    onClick={() => setQuery("")}
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <div className="nbs-filter-pop__opts">
                {visible.map((o) => (
                  <button
                    key={o.value || "__all__"}
                    type="button"
                    role="option"
                    aria-selected={o.value === value}
                    className={`nbs-filter-pop__opt nbs-hub-search-select__opt${
                      o.value === value ? " nbs-hub-search-select__opt--active" : ""
                    }`}
                    onMouseDown={(e) => {
                      // Prefer mousedown over click so the choice sticks before
                      // any outside-close / blur handlers run.
                      e.preventDefault();
                      onChange(o.value);
                      setOpen(false);
                    }}
                  >
                    {o.label}
                  </button>
                ))}
                {visible.length === 0 ? (
                  <p className="nbs-filter-pop__empty">Aucun résultat</p>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
